/**
 * Local WebAuthn / passkey for step-up (re-prompt) — Dashlane-style confirm.
 * Credential is device-bound; not a server login replacement.
 */
import { Preferences } from "@capacitor/preferences";

const CRED_KEY = "ciphernest.webauthn.credentialId";
const RP_ID_KEY = "ciphernest.webauthn.rpId";

function bufferToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function b64ToBuffer(b64: string): ArrayBuffer {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes.buffer;
}

export function webauthnAvailable(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential;
}

export async function hasPasskey(): Promise<boolean> {
  const { value } = await Preferences.get({ key: CRED_KEY });
  return Boolean(value);
}

export async function registerPasskey(email: string): Promise<void> {
  if (!webauthnAvailable()) throw new Error("Passkeys not supported in this browser");
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userId = new TextEncoder().encode(email.toLowerCase());
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge,
      rp: { name: "CipherNest", id: location.hostname },
      user: {
        id: userId,
        name: email,
        displayName: email,
      },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        userVerification: "required",
        residentKey: "preferred",
      },
      timeout: 60_000,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Passkey registration cancelled");
  await Preferences.set({
    key: CRED_KEY,
    value: bufferToB64(cred.rawId),
  });
  await Preferences.set({ key: RP_ID_KEY, value: location.hostname });
}

export async function assertPasskey(): Promise<boolean> {
  if (!webauthnAvailable()) return false;
  const { value: idB64 } = await Preferences.get({ key: CRED_KEY });
  if (!idB64) return false;
  const { value: rpId } = await Preferences.get({ key: RP_ID_KEY });
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  try {
    const cred = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: rpId || location.hostname,
        allowCredentials: [
          {
            type: "public-key",
            id: b64ToBuffer(idB64),
          },
        ],
        userVerification: "required",
        timeout: 60_000,
      },
    });
    return Boolean(cred);
  } catch {
    return false;
  }
}

export async function clearPasskey() {
  await Preferences.remove({ key: CRED_KEY });
  await Preferences.remove({ key: RP_ID_KEY });
}
